'use client';

import Link from 'next/link';
import React, { FC } from 'react';

import { Box, Burger, Flex, Group } from '@mantine/core';

import { AppName } from 'models';
import { AppHelpers } from 'helpers/AppHelpers';

import { useHeaderState } from 'components/header/hooks/use-header-state';
import { EIcon } from 'components/logo/EIcon';
import { ReplusLogo } from 'components/logo/ReplusLogo';
import { useCurrentUser } from 'hooks/use-current-user';
import { useSidebarNav } from 'hooks/use-sidebar-nav';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { NavigationService } from 'services/NavigationService';

import { AppSwitcherMenu } from './components/AppSwitcher';
import { BookmarksButton } from './components/BookmarksButton';
import { HeaderBannerMessage } from './components/HeaderBannerMessage';
import { HeaderPastDueNotice } from './components/HeaderPastDueNotice';
import { InboxButton } from './components/InboxButton';
import { JoinAsProButton } from './components/JoinAsProButton';
import { LoginButton } from './components/LoginButton';
import { Menu } from './components/Menu';
import { ReplusShowtimeLink } from './components/ReplusShowtimeLink';
import { SearchBar, SearchBarMobile } from './components/SearchBar';
import { StartForFreeButton } from './components/StartForFreeButton';
import { StaticHeaderActions } from './components/StaticHeaderActions';
import { UserIcon } from './components/UserIcon';

import cx from './Header.module.scss';

const Header: FC<{
    dynamic?: boolean;
    collapsable?: boolean;
}> = ({ dynamic = true, collapsable = false }) => {
    const user = useCurrentUser();

    const sidebarNav = useSidebarNav();

    const workspaceConfig = useWorkspaceConfig();
    const { showResourceProNav } = useHeaderState();

    return (
        <Flex
            align="center"
            justify="space-between"
            gap={{ base: 'xs', xs: 'md' }}
            classNames={cx}
            data-navigation
            data-collapsable={collapsable}
            data-replus={showResourceProNav || undefined}
        >
            <Flex component="nav" gap="xs" align="center">
                <Burger
                    className={cx.burger}
                    opened={sidebarNav.isOpen}
                    onClick={NavigationService.toggleMobileSidebar}
                    size="sm"
                />

                <Group wrap="nowrap" className={cx.logos} gap={0}>
                    <AppSwitcherMenu>
                        {showResourceProNav ? (
                            <Flex href={AppHelpers.getHomeUrl(AppName.REPLUS, !!user)} component={Link} c="black">
                                <ReplusLogo w="auto" h={26} mx={8} />
                            </Flex>
                        ) : (
                            <Flex href="/" component={Link} c="black">
                                {workspaceConfig.logoUrl ? (
                                    <img
                                        src={workspaceConfig.logoUrl}
                                        alt="Logo"
                                        height={30}
                                        className={cx.workspaceLogo}
                                    />
                                ) : (
                                    <EIcon height={36} />
                                )}
                            </Flex>
                        )}
                    </AppSwitcherMenu>
                    {showResourceProNav && <ReplusShowtimeLink />}
                </Group>

                <Menu dynamic={dynamic} />
                <SearchBar />
            </Flex>

            {dynamic && <HeaderBannerMessage />}
            {dynamic && <HeaderPastDueNotice />}

            <Flex gap={{ base: 4, xs: 'sm' }} align="center" className={cx.rightActions}>
                <SearchBarMobile />
                {dynamic ? (
                    <>
                        {user && <InboxButton />}
                        {user && <BookmarksButton />}
                        <UserIcon />
                        {!user && <LoginButton />}
                        {!user && (showResourceProNav ? <JoinAsProButton /> : <StartForFreeButton />)}
                    </>
                ) : (
                    <StaticHeaderActions />
                )}
            </Flex>
            <Box data-navigation-overlay />
        </Flex>
    );
};

export { Header };
